import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import { Camera, IdentificationCard, BookOpen, Star, ArrowsLeftRight, X, ClockCounterClockwise, UsersThree, PencilSimple, Trash, Key, Eye, EyeSlash, CalendarCheck, ListChecks, Printer } from "@phosphor-icons/react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { KidAvatar } from "@/components/KidAvatar";
import { HafalanRekap } from "@/components/HafalanRekap";
import { ReactionThread } from "@/components/ReactionThread";
import { PerkembanganRekap } from "@/components/PerkembanganRekap";
import { toast } from "sonner";

const TABS = [
  ["profil", "Profil", IdentificationCard],
  ["absensi", "Absensi", CalendarCheck],
  ["laporan", "Laporan", ClockCounterClockwise],
  ["hafalan", "Hafalan", BookOpen],
  ["evaluasi", "Evaluasi", ListChecks],
  ["perkembangan", "Predikat", Star],
];

async function getCroppedImg(src, area) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const canvas = document.createElement("canvas");
  canvas.width = area.width; canvas.height = area.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "profil");
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "guru";
  const canEditPhoto = isAdmin || user?.role === "orangtua";
  const [classes, setClasses] = useState([]);
  const [tOpen, setTOpen] = useState(false);
  const [tMode, setTMode] = useState("kelas"); // "kelas" | "guru"
  const [tGroup, setTGroup] = useState("TK B");
  const [tSub, setTSub] = useState("");
  const [tTeacher, setTTeacher] = useState("");
  const [eOpen, setEOpen] = useState(false);
  const [ef, setEf] = useState({});
  const [delOpen, setDelOpen] = useState(false);
  const [cred, setCred] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [credForm, setCredForm] = useState({ username: "", password: "" });
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => { setS(null); setErr(false); api.get(`/students/${id}`).then((r) => setS(r.data)).catch((e) => { setErr(e.response?.status === 403 ? "Anda tidak memiliki akses ke data ananda ini." : "Gagal memuat data ananda."); }); }, [id]);
  useEffect(() => { if (isAdmin) api.get("/classes").then((r) => setClasses(r.data)); }, [isAdmin]);
  useEffect(() => {
    if (isAdmin && s) {
      api.get(`/students/${id}/parent-credentials`).then((r) => setCred(r.data)).catch(() => {});
    }
  }, [isAdmin, s, id]);

  const saveCred = async () => {
    if (!credForm.username.trim()) { toast.error("Username wajib diisi"); return; }
    try {
      const { data } = await api.put(`/students/${id}/parent-credentials`, { username: credForm.username.trim(), password: credForm.password || null });
      setCred(data); setCredOpen(false); toast.success("Akun orang tua diperbarui");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const GROUPS = [...new Set(classes.map((c) => c.group))];
  const groupOf = (k) => (k?.startsWith("Kelas ") ? "Kelas " + k.split(" ")[1][0] : k?.startsWith("TK B") ? "TK B" : k?.startsWith("TK A") ? "TK A" : "KB");
  const subsOf = (g) => classes.filter((c) => c.group === g);
  const teachersOf = (k) => (classes.find((c) => c.kelas === k)?.teachers) || [];
  const openTransfer = (mode) => { setTMode(mode); setTGroup(groupOf(s.kelas)); setTSub(s.kelas); setTTeacher(s.teacher_id || ""); setTOpen(true); };
  const saveTransfer = async () => {
    const kelas = tMode === "guru" ? s.kelas : tSub;
    if (!kelas || !tTeacher) { toast.error("Pilih sub-kelas & Umi Guru"); return; }
    try {
      const { data } = await api.post(`/students/${id}/transfer`, { kelas, teacher_id: tTeacher });
      setS(data); setTOpen(false);
      toast.success(tMode === "guru" ? "Umi Guru siswa diperbarui" : "Siswa berhasil dipindahkan");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = () => { setEf({ ...s }); setEOpen(true); };
  const delStudent = async () => {
    try { await api.delete(`/students/${id}`); toast.success("Data murid dihapus"); navigate(-1); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    if (!ef.name?.trim() || !ef.nis?.trim()) { toast.error("Nama & NIS wajib diisi"); return; }
    const body = {
      name: ef.name, nis: ef.nis, student_number: ef.student_number || null, kelas: s.kelas,
      gender: ef.gender || "Laki-laki", birth_place: ef.birth_place || null, birth_date: ef.birth_date || null,
      address: ef.address || null, father_name: ef.father_name || null, mother_name: ef.mother_name || null,
      parent_phone: ef.parent_phone || null, parent_id: s.parent_id || null, teacher_id: s.teacher_id || null,
    };
    try { const { data } = await api.put(`/students/${id}`, body); setS(data); setEOpen(false); toast.success("Profil Ananda diperbarui"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(f.type)) { toast.error("Format harus JPG atau PNG"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Ukuran foto maksimal 5MB"); return; }
    setCropSrc(URL.createObjectURL(f)); setCropOpen(true);
  };
  const applyCrop = async () => {
    try {
      const blob = await getCroppedImg(cropSrc, croppedAreaPixels);
      if (blob.size > 3 * 1024 * 1024) { toast.error("Hasil terlalu besar, kecilkan zoom"); return; }
      setFile(new File([blob], "foto.jpg", { type: "image/jpeg" })); setPreview(URL.createObjectURL(blob)); setCropOpen(false);
    } catch (e) { toast.error("Gagal memproses foto"); }
  };
  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/students/${id}/photo`, fd);
      setS(data); setPreview(null); setFile(null);
      toast.success("Foto siswa berhasil disimpan");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (err) return <div><PageHeader title="Detail Ananda" /><p data-testid="student-load-error" className="text-center text-stone-400 mt-10 px-8">{err}</p></div>;
  if (!s) return <div><PageHeader title="Detail Ananda" /><p className="text-center text-stone-400 mt-10">Memuat...</p></div>;

  const isSD = s.kelas?.startsWith("Kelas ");
  const rows = [
    ["Nomor Siswa", s.student_number || "-"], ["NIS", s.nis], ["Kelas", s.kelas], ["Jenis Kelamin", s.gender],
    ["Tempat, Tgl Lahir", `${s.birth_place || "-"}${s.birth_date ? ", " + new Date(s.birth_date).toLocaleDateString("id-ID") : ""}`],
    ["Alamat", s.address || "-"], ["Nama Ayah", s.father_name || "-"], ["Nama Ibu", s.mother_name || "-"],
    ["No. HP Orang Tua", s.parent_phone || "-"],
  ];

  return (
    <div>
      <PageHeader title="Detail Ananda" />
      <div className="px-5 pt-6 flex flex-col items-center">
        <div className="relative">
          <KidAvatar gender={s.gender} size={96} src={preview || s.avatar} className="shadow-md ring-4 ring-white" />
          {canEditPhoto && (
            <button data-testid="student-photo-pick-btn" onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shadow-lg border-2 border-white active:scale-90 transition-transform" aria-label="Ubah foto">
              <Camera size={18} weight="fill" />
            </button>
          )}
          <input ref={fileRef} data-testid="student-photo-input" type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} />
        </div>
        <h2 className="font-heading text-xl font-bold text-stone-800 mt-4">{s.name}</h2>
        <p className="text-stone-500">Kelas {s.kelas}</p>
        {isAdmin && !preview && (
          <div className="flex gap-2 mt-3">
            <button data-testid="pindah-kelas-btn" onClick={() => openTransfer("kelas")}
              className="flex items-center gap-1.5 bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full active:scale-95 transition-transform">
              <ArrowsLeftRight size={16} weight="bold" /> Pindah Kelas
            </button>
            <button data-testid="pindah-guru-btn" onClick={() => openTransfer("guru")}
              className="flex items-center gap-1.5 bg-secondary/20 text-stone-700 text-sm font-semibold px-4 py-2 rounded-full active:scale-95 transition-transform">
              <UsersThree size={16} weight="bold" /> Pindah Guru
            </button>
          </div>
        )}
        {preview && (
          <div className="flex gap-2 mt-3">
            <button data-testid="student-photo-save-btn" onClick={save} disabled={saving}
              className="bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-full active:scale-95 transition-transform disabled:opacity-60">
              {saving ? "Menyimpan..." : "Simpan Foto"}
            </button>
            <button data-testid="student-photo-cancel-btn" onClick={() => { setPreview(null); setFile(null); }}
              className="bg-stone-100 text-stone-600 text-sm font-semibold px-5 py-2.5 rounded-full active:scale-95 transition-transform">Batal</button>
          </div>
        )}
      </div>

      {/* tabs */}
      <div className="px-5 mt-5">
        <button data-testid="cetak-rapor-btn" onClick={async () => {
          const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow);
          const start = d.toISOString().slice(0, 10);
          const w = window.open("", "_blank");
          try {
            const res = await fetch(`${API}/reports/weekly.html?student_id=${id}&start=${start}&origin=${encodeURIComponent(window.location.origin)}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
            const html = await res.text();
            w.document.open(); w.document.write(html); w.document.close();
          } catch (e) { if (w) w.close(); }
        }}
          className="w-full mb-3 border-2 border-primary text-primary font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Printer size={18} weight="bold" /> Cetak Rapor Mingguan (PDF)
        </button>
        <div className="grid grid-cols-3 gap-2 bg-white border border-stone-200/60 rounded-2xl p-1.5 shadow-sm">
          {TABS.map(([v, l, Icon]) => (
            <button key={v} data-testid={`sp-tab-${v}`} onClick={() => setTab(v)}
              className={`py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === v ? "bg-primary text-white shadow" : "text-stone-500"}`}>
              <Icon size={16} weight={tab === v ? "fill" : "regular"} /> {l}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-4 pb-8">
        {tab === "profil" && (
          <>
            {isAdmin && (
              <button data-testid="edit-profil-btn" onClick={openEdit}
                className="w-full mb-3 border-2 border-primary/40 text-primary font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
                <PencilSimple size={16} weight="bold" /> Edit Profil Ananda
              </button>
            )}
            {isAdmin && (
              <button data-testid="export-student-report-btn" onClick={async () => {
                try {
                  const res = await api.get(`/admin/student-report.xlsx?student_id=${id}`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a"); a.href = url; a.download = `Laporan_${s?.name || "siswa"}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                  toast.success("Laporan lengkap diunduh");
                } catch (e) { toast.error("Gagal export laporan"); }
              }} className="w-full mb-3 bg-primary text-white font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
                <IdentificationCard size={16} weight="bold" /> Export Laporan Lengkap
              </button>
            )}
            <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm divide-y divide-stone-100 overflow-hidden">
              {rows.map(([k, v]) => (
                <div key={k} data-testid={`profile-row-${k}`} className="flex px-4 py-3">
                  <span className="text-stone-400 text-sm w-40 shrink-0">{k}</span>
                  <span className="text-stone-700 text-sm font-medium">{v}</span>
                </div>
              ))}
            </div>
            {isAdmin && cred && (
              <div className="mt-4 bg-white rounded-3xl border border-blue-200 shadow-sm p-4" data-testid="parent-credentials-card">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="font-heading font-semibold text-stone-800 flex items-center gap-2"><Key size={18} weight="duotone" className="text-blue-600" /> Akun Orang Tua</p>
                  <button data-testid="edit-parent-cred-btn" onClick={() => { setCredForm({ username: cred.username || "", password: "" }); setCredOpen(true); }}
                    className="text-blue-600 text-sm font-semibold flex items-center gap-1 active:scale-95 transition-transform"><PencilSimple size={14} weight="bold" /> Ubah</button>
                </div>
                <div className="flex px-1 py-1.5"><span className="text-stone-400 text-sm w-28 shrink-0">Username</span><span className="text-stone-700 text-sm font-medium break-all" data-testid="parent-cred-username">{cred.username || "-"}</span></div>
                <div className="flex px-1 py-1.5 items-center"><span className="text-stone-400 text-sm w-28 shrink-0">Password</span>
                  <span className="text-stone-700 text-sm font-medium" data-testid="parent-cred-password">{showPw ? (cred.password || "-") : "••••••"}</span>
                  <button data-testid="toggle-parent-pw" onClick={() => setShowPw((v) => !v)} className="ml-2 text-blue-600 active:scale-90 transition-transform" aria-label="Tampilkan sandi">{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                </div>
                {cred.has_account && !cred.password && (
                  <p className="text-[11px] text-amber-600 mt-0.5">Password tidak dapat ditampilkan untuk akun lama. Gunakan "Ubah" untuk mengatur ulang.</p>
                )}
                <p className="text-[11px] text-stone-400 mt-1">Orang tua login di portal {isSD ? "SDQu" : "TK"} memakai username & password ini.</p>
              </div>
            )}
            {s.transfer_history?.length > 0 && (
              <div className="mt-4" data-testid="transfer-history">
                <p className="font-heading font-semibold text-stone-800 mb-2 flex items-center gap-2">
                  <ClockCounterClockwise size={18} weight="duotone" className="text-primary" /> Riwayat Perpindahan
                </p>
                <div className="space-y-2">
                  {[...s.transfer_history].reverse().map((h, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm text-sm">
                      <p className="text-stone-700 font-medium">
                        {h.from_kelas || "-"}{h.from_teacher ? ` (${h.from_teacher})` : ""} → {h.to_kelas}{h.to_teacher ? ` (${h.to_teacher})` : ""}
                      </p>
                      <p className="text-stone-400 text-xs mt-0.5">{new Date(h.date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isAdmin && (
              <button data-testid="delete-student-btn" onClick={() => setDelOpen(true)}
                className="w-full mt-4 border-2 border-destructive/40 text-destructive font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
                <Trash size={16} weight="bold" /> Hapus Murid
              </button>
            )}
          </>
        )}
        {tab === "absensi" && <AbsensiRekap studentId={id} canEdit={canEdit} />}
        {tab === "laporan" && <LaporanRekap studentId={id} canEdit={canEdit} />}
        {tab === "hafalan" && <HafalanRekap studentId={id} canEdit={canEdit} />}
        {tab === "evaluasi" && <EvaluasiRekap studentId={id} />}
        {tab === "perkembangan" && <PerkembanganRekap studentId={id} canEdit={canEdit} />}
      </div>

      {tOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setTOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">{tMode === "guru" ? "Pindah Umi Guru" : "Pindah Kelas"} • {s.name}</h3>
              <button onClick={() => setTOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            {tMode === "kelas" && (
              <>
                <p className="text-xs text-stone-500 mb-1">Kelompok</p>
                <select data-testid="transfer-group" value={tGroup} onChange={(e) => { setTGroup(e.target.value); setTSub(""); setTTeacher(""); }}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary">
                  {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <p className="text-xs text-stone-500 mb-1">Sub-kelas</p>
                <select data-testid="transfer-sub" value={tSub} onChange={(e) => { setTSub(e.target.value); setTTeacher(""); }}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary">
                  <option value="">Pilih sub-kelas</option>
                  {subsOf(tGroup).map((c) => <option key={c.kelas} value={c.kelas} disabled={!c.teachers?.length}>{c.kelas}{c.teachers?.length ? "" : " (belum ada guru)"}</option>)}
                </select>
              </>
            )}
            <p className="text-xs text-stone-500 mb-1">Umi Guru</p>
            <select data-testid="transfer-teacher" value={tTeacher} onChange={(e) => setTTeacher(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary">
              <option value="">Pilih Umi Guru</option>
              {teachersOf(tMode === "guru" ? s.kelas : tSub).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="transfer-save" onClick={saveTransfer}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan</button>
          </div>
        </div>
      )}

      {eOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setEOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Edit Profil Ananda</h3>
              <button onClick={() => setEOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            {[
              ["name", "Nama Lengkap", "text"], ["nis", "NIS", "text"], ["student_number", "Nomor Siswa", "text"],
              ["birth_place", "Tempat Lahir", "text"], ["birth_date", "Tanggal Lahir", "date"], ["address", "Alamat", "text"],
              ["father_name", "Nama Ayah", "text"], ["mother_name", "Nama Ibu", "text"], ["parent_phone", "No. HP Orang Tua", "text"],
            ].map(([k, label, type]) => (
              <div key={k} className="mb-3">
                <p className="text-xs text-stone-500 mb-1">{label}</p>
                <input data-testid={`edit-${k}`} type={type} value={ef[k] || ""} onChange={(e) => setEf({ ...ef, [k]: e.target.value })}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary" />
              </div>
            ))}
            <p className="text-xs text-stone-500 mb-1">Jenis Kelamin</p>
            <select data-testid="edit-gender" value={ef.gender || "Laki-laki"} onChange={(e) => setEf({ ...ef, gender: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-2 outline-none focus:border-primary">
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>
            <p className="text-[11px] text-stone-400 mb-4">Kelas: {s.kelas} — ubah lewat "Pindah Kelas".</p>
            <button data-testid="edit-save-btn" onClick={saveEdit}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan Perubahan</button>
          </div>
        </div>
      )}

      {cropOpen && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-end md:items-center justify-center" data-testid="crop-modal">
          <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg text-stone-800">Atur Foto</h3>
              <button onClick={() => setCropOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="relative w-full h-64 bg-stone-900 rounded-2xl overflow-hidden">
              <Cropper image={cropSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setCroppedAreaPixels(px)} />
            </div>
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="crop-zoom" className="w-full mt-3 accent-primary" />
            <button data-testid="crop-apply-btn" onClick={applyCrop}
              className="w-full mt-2 bg-primary text-white font-semibold py-3 rounded-full active:scale-95 transition-transform">Terapkan</button>
          </div>
        </div>
      )}

      {credOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setCredOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Ubah Akun Orang Tua</h3>
              <button onClick={() => setCredOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-500 mb-1">Username</p>
            <input data-testid="cred-username-input" value={credForm.username} onChange={(e) => setCredForm({ ...credForm, username: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-500 mb-1">Password baru (kosongkan bila tidak diubah)</p>
            <input data-testid="cred-password-input" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })}
              placeholder="min. 6 karakter" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="cred-save-btn" onClick={saveCred}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan</button>
          </div>
        </div>
      )}

      {delOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center px-6" onClick={() => setDelOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-3xl p-5 text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><Trash size={26} weight="bold" className="text-destructive" /></div>
            <h3 className="font-heading font-bold text-lg text-stone-800">Hapus Murid?</h3>
            <p className="text-sm text-stone-500 mt-1 mb-4">Apakah yakin menghapus data <span className="font-semibold">{s.name}</span>? Data tidak dapat dikembalikan.</p>
            <div className="flex gap-2">
              <button onClick={() => setDelOpen(false)} className="flex-1 bg-stone-100 text-stone-600 font-semibold py-3 rounded-full">Batal</button>
              <button data-testid="delete-student-confirm" onClick={delStudent} className="flex-1 bg-destructive text-white font-semibold py-3 rounded-full active:scale-95 transition-transform">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LaporanRekap({ studentId, canEdit }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [evalT, setEvalT] = useState([]);
  const [kegT, setKegT] = useState([]);
  useEffect(() => { api.get(`/reports/daily?student_id=${studentId}`).then((r) => setItems([...r.data].sort((a, b) => (b.date || "").localeCompare(a.date || "")))).catch(() => {}); }, [studentId]);
  const loadThreads = (date) => {
    api.get(`/progress?student_id=${studentId}&category=evaluasi_harian`).then((r) => setEvalT((r.data.find((x) => x.item_key === date) || {}).thread || [])).catch(() => {});
    api.get(`/progress?student_id=${studentId}&category=kegiatan_harian`).then((r) => setKegT((r.data.find((x) => x.item_key === date) || {}).thread || [])).catch(() => {});
  };
  const open = (r) => { setSel(r); loadThreads(r.date); };
  const stColor = (st) => st === "Hadir" ? "bg-green-100 text-green-700" : st === "Terlambat" ? "bg-amber-100 text-amber-700" : st === "Izin" || st === "Sakit" ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-600";
  const fmt = (d) => new Date(d).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  if (!items.length) return <p className="text-center text-stone-400 text-sm py-8" data-testid="laporan-empty">Belum ada laporan harian.</p>;
  return (
    <div className="space-y-2" data-testid="laporan-rekap">
      {items.map((r, i) => (
        <button key={i} data-testid={`laporan-item-${i}`} onClick={() => open(r)}
          className="w-full text-left bg-white rounded-2xl border border-stone-200/60 shadow-sm p-3 active:scale-[0.99] transition-transform flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-stone-700 text-sm">{fmt(r.date)}</p>
            <p className="text-xs text-stone-400">Akhlak: {r.akhlak || "-"}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stColor(r.attendance_status)}`}>{r.attendance_status}</span>
            <ClockCounterClockwise size={16} className="text-stone-300" />
          </div>
        </button>
      ))}

      {sel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setSel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[90vh] overflow-y-auto" data-testid="laporan-detail-modal">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading font-bold text-base text-stone-800">Laporan Harian</h3>
                <p className="text-xs text-stone-400">{fmt(sel.date)}</p>
              </div>
              <button onClick={() => setSel(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="space-y-2.5 mb-4">
              <div className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2.5">
                <span className="text-xs text-stone-400">Kehadiran</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${stColor(sel.attendance_status)}`}>{sel.attendance_status}</span>
              </div>
              <div className="bg-stone-50 rounded-xl px-3 py-2.5">
                <p className="text-xs text-stone-400 mb-0.5">Hafalan Hari Ini</p>
                <p className="text-sm text-stone-700">{sel.hafalan_today || "-"}</p>
              </div>
              <div className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2.5">
                <span className="text-xs text-stone-400">Perilaku / Akhlak</span>
                <span className="text-sm font-semibold text-stone-700">{sel.akhlak || "-"}</span>
              </div>
              <div className="bg-stone-50 rounded-xl px-3 py-2.5">
                <p className="text-xs text-stone-400 mb-0.5">Catatan Guru</p>
                <p className="text-sm text-stone-700">{sel.note || "-"}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200/60 p-3 mb-2.5">
              <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Hafalan Hari Ini</p>
              <ReactionThread studentId={studentId} category="evaluasi_harian" itemKey={sel.date} itemLabel="Hafalan Hari Ini" thread={evalT} onPosted={() => loadThreads(sel.date)} canEdit={canEdit} />
            </div>
            <div className="bg-white rounded-2xl border border-stone-200/60 p-3">
              <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Catatan Guru Hari Ini</p>
              <ReactionThread studentId={studentId} category="kegiatan_harian" itemKey={sel.date} itemLabel="Catatan Guru Hari Ini" thread={kegT} onPosted={() => loadThreads(sel.date)} canEdit={canEdit} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AbsensiRekap({ studentId, canEdit }) {
  const [items, setItems] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [form, setForm] = useState({ attendance_status: "Hadir", hafalan_today: "", akhlak: "Baik", note: "" });
  const [saving, setSaving] = useState(false);
  const [evalThread, setEvalThread] = useState([]);
  const [kegThread, setKegThread] = useState([]);
  const reqRef = useRef(0);
  const STATUS = ["Hadir", "Izin", "Sakit", "Alpha"];
  const AKHLAK = ["Kurang", "Cukup", "Baik", "Sangat Baik"];
  const loadList = () => api.get(`/attendance?student_id=${studentId}`).then((r) => setItems([...r.data].sort((a, b) => (b.date || "").localeCompare(a.date || "")))).catch(() => {});
  const loadThreads = () => {
    api.get(`/progress?student_id=${studentId}&category=evaluasi_harian`).then((r) => setEvalThread((r.data.find((x) => x.item_key === day) || {}).thread || [])).catch(() => {});
    api.get(`/progress?student_id=${studentId}&category=kegiatan_harian`).then((r) => setKegThread((r.data.find((x) => x.item_key === day) || {}).thread || [])).catch(() => {});
  };
  useEffect(() => { loadList(); }, [studentId]);
  useEffect(() => {
    const seq = ++reqRef.current;
    api.get(`/reports/daily?student_id=${studentId}&date=${day}`).then((r) => {
      if (seq !== reqRef.current) return;
      const rep = r.data[0];
      if (rep) setForm({ attendance_status: rep.attendance_status, hafalan_today: rep.hafalan_today || "", akhlak: rep.akhlak, note: rep.note || "" });
      else setForm({ attendance_status: "Hadir", hafalan_today: "", akhlak: "Baik", note: "" });
    }).catch(() => {});
    loadThreads(); // eslint-disable-next-line
  }, [studentId, day]);
  const save = async () => {
    setSaving(true);
    try { await api.post("/reports/daily", { student_id: studentId, date: day, ...form }); toast.success("Laporan harian tersimpan"); loadList(); }
    catch (e) { toast.error("Gagal menyimpan laporan"); }
    setSaving(false);
  };
  const stColor = (st) => st === "Hadir" ? "bg-green-100 text-green-700" : st === "Izin" || st === "Sakit" ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-600";
  const fmt = (d) => new Date(d).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const count = (st) => items.filter((r) => r.status === st).length;
  const SUMMARY = [["Hadir", "text-green-700 bg-green-50"], ["Izin", "text-sky-700 bg-sky-50"], ["Sakit", "text-sky-700 bg-sky-50"], ["Alpha", "text-red-600 bg-red-50"]];
  const btn = (active) => `py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-primary text-white shadow" : "bg-stone-50 text-stone-500 border border-stone-200"} ${!canEdit ? "opacity-70" : ""}`;
  if (!canEdit) {
    const cutoff = (() => { const d = new Date(); d.setDate(d.getDate() - 31); return d.toISOString().slice(0, 10); })();
    const recent = items.filter((r) => (r.date || "") >= cutoff);
    return (
      <div data-testid="absensi-rekap">
        <div className="flex items-center justify-between px-3 py-2 text-[11px] font-semibold text-stone-400 uppercase tracking-wide">
          <span>Tanggal</span><span>Kehadiran</span>
        </div>
        {recent.length === 0 ? (
          <p className="text-center text-stone-400 text-sm py-6" data-testid="absensi-empty">Belum ada data absensi 1 bulan terakhir.</p>
        ) : (
          <div className="space-y-2">
            {recent.map((r, i) => (
              <div key={i} data-testid={`absensi-item-${i}`} className="w-full bg-white rounded-2xl border border-stone-200/60 shadow-sm px-3 py-2.5 flex items-center justify-between gap-2">
                <p className="font-semibold text-stone-700 text-sm truncate">{fmt(r.date)}</p>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full shrink-0 ${stColor(r.status)}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div data-testid="absensi-rekap">
      <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 mb-4" data-testid="absensi-input">
        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Tanggal</p>
        <input type="date" data-testid="absensi-date" value={day} onChange={(e) => setDay(e.target.value)}
          className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2.5 px-3 mb-4 outline-none focus:border-primary text-sm" />

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Kehadiran</p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {STATUS.map((s) => (
            <button key={s} data-testid={`absensi-opt-${s}`} disabled={!canEdit} onClick={() => setForm({ ...form, attendance_status: s })} className={btn(form.attendance_status === s)}>{s}</button>
          ))}
        </div>

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Hafalan Hari Ini</p>
        <textarea data-testid="absensi-hafalan" value={form.hafalan_today} disabled={!canEdit} onChange={(e) => setForm({ ...form, hafalan_today: e.target.value })} rows={3}
          placeholder="Contoh: Surah Al-Ikhlas ayat 1-4"
          className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary resize-none text-sm disabled:opacity-80" />

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Perilaku / Akhlak</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {AKHLAK.map((a) => (
            <button key={a} data-testid={`absensi-akhlak-${a}`} disabled={!canEdit} onClick={() => setForm({ ...form, akhlak: a })} className={btn(form.akhlak === a)}>{a}</button>
          ))}
        </div>

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Catatan Guru</p>
        <textarea data-testid="absensi-note" value={form.note} disabled={!canEdit} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3}
          placeholder="Catatan aktivitas & perkembangan anak hari ini..."
          className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary resize-none text-sm disabled:opacity-80" />

        <div className="space-y-3 mb-4">
          <div className="bg-white rounded-2xl border border-stone-200/60 p-3">
            <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Hafalan Hari Ini</p>
            <ReactionThread studentId={studentId} category="evaluasi_harian" itemKey={day} itemLabel="Hafalan Hari Ini" thread={evalThread} onPosted={loadThreads} canEdit={canEdit} />
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/60 p-3">
            <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Catatan Guru Hari Ini</p>
            <ReactionThread studentId={studentId} category="kegiatan_harian" itemKey={day} itemLabel="Catatan Guru Hari Ini" thread={kegThread} onPosted={loadThreads} canEdit={canEdit} />
          </div>
        </div>

        {canEdit && (
          <button data-testid="absensi-save" onClick={save} disabled={saving}
            className="w-full bg-primary text-white font-semibold py-3.5 rounded-2xl active:scale-95 transition-transform disabled:opacity-60">
            {saving ? "Menyimpan..." : "Simpan Laporan Harian"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4">
        {SUMMARY.map(([st, cls]) => (
          <div key={st} data-testid={`absensi-count-${st}`} className={`rounded-2xl py-2.5 text-center ${cls}`}>
            <p className="text-lg font-bold">{count(st)}</p>
            <p className="text-[11px] font-medium">{st}</p>
          </div>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="text-center text-stone-400 text-sm py-6" data-testid="absensi-empty">Belum ada data absensi.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r, i) => (
            <div key={i} data-testid={`absensi-item-${i}`} className="w-full bg-white rounded-2xl border border-stone-200/60 shadow-sm p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <CalendarCheck size={18} className="text-stone-300 shrink-0" />
                <p className="font-semibold text-stone-700 text-sm truncate">{fmt(r.date)}</p>
              </div>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full shrink-0 ${stColor(r.status)}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



const EVAL_CATS = [["tahfidz", "Tahfidz"], ["doa", "Doa"], ["hadits", "Hadits"], ["arab", "B. Arab"], ["inggris", "B. Inggris"], ["lainnya", "Lainnya"]];
function EvaluasiRekap({ studentId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/progress?student_id=${studentId}`).then((r) => setRows(r.data)).catch(() => {}); }, [studentId]);
  const goItem = (it) => {
    if (it.category === "tahfidz") navigate("/hafalan");
    else navigate(`/materi?tab=${it.category}&item=${encodeURIComponent(it.item_key || "")}`);
  };
  const Group = ({ title, status, tone }) => {
    const items = rows.filter((r) => r.status === status);
    return (
      <div className="mb-5" data-testid={`eval-group-${status}`}>
        <div className={`flex items-center gap-2 mb-2 font-heading font-bold ${tone}`}>
          <ListChecks size={18} weight="fill" /> {title}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden divide-y divide-stone-100">
          {EVAL_CATS.map(([key, label]) => {
            const its = items.filter((r) => r.category === key);
            if (!its.length) return null;
            return (
              <div key={key} className="p-3" data-testid={`eval-${status}-${key}`}>
                <p className="text-[11px] uppercase tracking-wide text-stone-400 mb-1">{label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {its.map((it, i) => (
                    <button key={i} data-testid={`eval-item-${status}-${key}-${i}`} onClick={() => goItem(it)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full underline decoration-dotted underline-offset-2 active:scale-95 transition-transform ${status === "lancar" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {it.item_label || it.item_key}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {items.length === 0 && <p className="text-center text-stone-400 text-sm py-4">Belum ada data.</p>}
        </div>
      </div>
    );
  };
  return (
    <div data-testid="evaluasi-rekap">
      <Group title="Hafalan / Capaian Lancar" status="lancar" tone="text-green-600" />
      <Group title="Perlu Diulang" status="ulang" tone="text-amber-600" />
    </div>
  );
}

